import * as React from 'react';

import { cn } from '@/components/atoms/primitives/utils';

/** Public-file path for the national Electric Amber lettermark. */
export const NATIONAL_LETTERMARK_SRC = '/brand/loci-lettermark.svg';

export interface LociLettermarkProps {
  className?: string;
  'data-testid'?: string;
  /**
   * Accessible name. When set, the SVG is `role="img"` with that label.
   * Omit to keep the default decorative `aria-hidden` (AppHeader).
   */
  alt?: string;
}

/**
 * LociLettermark — national wordmark as inline SVG (WEB-689 / DPL-42;
 * retokened FRE-1361 / DES-85, 2026-09-27).
 *
 * Fills with `currentColor` so chrome can select `--nav-lettermark`
 * (ink `--nav-fg` on light paper, cream `--loci-cream` on dark) without a
 * second asset file and without `filter: brightness()`. The v2 lockup rule
 * pairs the lettermark with the theme-invariant brand-amber
 * {@link LociDotsMark} instead of tinting the wordmark itself amber — see
 * `styles/tokens/color.css` for the token. Decorative by default (`aria-hidden`)
 * — AppHeader sits it beside a visible city/layer label. Nav lockups pass
 * `alt` so the mark keeps an accessible name. `LociLettermarkMask` (this
 * folder's sibling, a CSS-masked `<span>`) is the preferred implementation
 * for new lockups; this inline-SVG version stays where it already ships.
 */
export function LociLettermark({
  className,
  'data-testid': testId = 'loci-lettermark',
  alt,
}: LociLettermarkProps): React.ReactElement {
  const named = Boolean(alt);
  return (
    <svg
      viewBox="0 0 445.12 140.08"
      xmlns="http://www.w3.org/2000/svg"
      className={cn('h-5 w-auto text-nav-lettermark', className)}
      aria-hidden={named ? undefined : true}
      role={named ? 'img' : undefined}
      aria-label={named ? alt : undefined}
      data-testid={testId}
    >
      <polygon
        points="96.74 114.15 96.7 138.58 .06 138.56 0 1.56 26.57 1.54 26.59 114.12 96.74 114.15"
        fill="currentColor"
      />
      <rect x="418.3" y="1.54" width="26.82" height="137.04" fill="currentColor" />
      <path
        d="M250.28,70.04c0,38.68-31.38,70.04-70.1,70.04s-70.1-31.36-70.1-70.04S141.47,0,180.18,0s70.1,31.36,70.1,70.04ZM193.49,113.58c14.33-4.56,24.66-16.19,28.43-30.45,1.92-7.26,2.37-14.43.97-21.83-1.32-6.96-3.71-13.48-7.9-19.26-5.34-7.37-12.86-12.92-21.64-15.63-9.64-2.98-20.1-2.62-29.47,1.13-11.77,4.71-20.62,14.53-24.57,26.48-4.19,12.64-3.46,26.07,2.56,37.94,4.94,9.76,13.24,17.52,23.67,21.18,8.92,3.14,18.81,3.35,27.96.44Z"
        fill="currentColor"
      />
      <path
        d="M298,65.55c-1.98,22.94,11.64,43.03,34.03,48.68,7.42,1.6,14.83,1.55,22.21-.31,9.47-2.65,17.7-8.08,23.94-15.78l17.99,17.67c-10.17,11.64-23.84,19.68-39.06,22.88-25.52,4.92-51.06-3.33-68.25-22.83-8.68-9.84-14.21-21.78-16.3-34.84-2.37-14.8.12-30.32,7.1-43.54C292.28,13.59,317.57-.77,344.5.05c8.3.25,16.14,1.77,23.9,4.8,10.7,4.19,20.23,10.66,27.81,19.35l-17.55,17.3c-.12.12-.64.08-.63-.04-6.09-6.94-13.46-12.42-22.36-15-15.33-4.43-31.96-.97-43.63,10.04-8.18,7.72-13.06,17.79-14.03,29.04Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** True when `src` is the national lettermark file (theme via `--nav-lettermark`). */
export function isNationalLettermarkSrc(src: string): boolean {
  return src === NATIONAL_LETTERMARK_SRC;
}
